import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Building2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';

const SECTIONS = [
  { n: 1, title: 'Introduction', body: [
    'This website is operated by Motion Reality Sim Racing Ltd.',
    'References to "we", "us" and "our" mean Motion Reality Sim Racing Ltd.',
    'By visiting our website and/or purchasing from us, you accept these Terms and Conditions and our Privacy Policy.',
    'The most current version of these Terms and Conditions is always available on this page.',
    'We may update, change or replace any part of these Terms without prior notice. It is your responsibility to check this page regularly.',
  ] },
  { n: 2, title: 'General Conditions', body: [
    'We reserve the right to refuse service to anyone, for any reason, at any time.',
    'Content entered on this website may be transferred unencrypted across networks and adapted for technical requirements.',
    'You may not reproduce, duplicate, copy, sell, resell or exploit any part of this website or its services for commercial gain.',
    'Headings and titles are for convenience only and do not affect interpretation of these Terms.',
  ] },
  { n: 3, title: 'Online Store Terms', body: [
    'Our website offers products and services for sale ("items").',
    'We take reasonable care to ensure item details are correct at the time of entry.',
    'Images, colours, textures or finishes may differ from actual goods due to display variations.',
    'Orders may be declined for reasons including:',
  ], list: [
    'Item out of stock',
    'Payment not authorised or suspected fraud',
    'Pricing or description error',
    'System or procurement failure',
    'Failed customer validation checks',
  ], after: [
    'Order confirmation emails are acknowledgements only, not acceptance.',
    'Orders are accepted only once goods are dispatched.',
  ] },
  { n: 4, title: 'Modifications to Service and Pricing', body: [
    'Prices are subject to change without notice.',
    'We may modify or discontinue any part of the service or content without notice.',
    'We accept no liability for modifications, price changes, suspension or discontinuation.',
  ] },
  { n: 5, title: 'Delivery', body: [
    'Delivery options may be presented at checkout.',
    'Timescales provided are estimates only; delays may occur beyond our control.',
    '"Next day delivery" orders placed Friday–Sunday may not be dispatched until Monday, arriving Tuesday.',
  ] },
  { n: 6, title: 'Order Cancellation', body: [
    'Contact us immediately if you wish to cancel an order.',
    'If items have been dispatched, you must return them at your own cost.',
    'If items have not been dispatched, our Refund Policy applies.',
    'Personalised, perishable or time‑critical items (e.g. food, newspapers, event tickets, e‑vouchers) may not be eligible for cancellation.',
  ] },
  { n: 7, title: 'Liability and Indemnity', body: [
    'We are not liable for direct, indirect, special or consequential damages, including loss of profit or opportunity, arising from use of or inability to use items purchased.',
    'You agree to indemnify us and our staff against all claims, damages, costs and expenses (including legal fees) resulting from any breach of these Terms.',
  ] },
  { n: 8, title: 'Jurisdiction', body: [
    'These Terms are governed by English Law.',
    'The courts of England & Wales have exclusive jurisdiction.',
  ] },
  { n: 9, title: 'Entire Agreement', body: [
    'These Terms, together with our Privacy Policy and Refund Policy, constitute the entire agreement between you and us.',
    'They supersede all prior agreements, written or oral.',
  ] },
  { n: 10, title: 'General Provisions', body: [
    'If any term is found unenforceable, the remainder shall continue in full force.',
    'We are not responsible for delays or failures caused by circumstances beyond our reasonable control.',
  ] },
  { n: 12, title: 'Right to Cancel (Cooling‑Off Period)', body: [
    'Consumers purchasing online have the right to cancel within 14 days of receiving goods or entering into a service contract.',
    'Notification must be made in writing (email acceptable) within the 14‑day period.',
    'Goods must be returned within a further 14 days.',
    'Refunds cover the cost of goods and standard delivery charges.',
    'Customers are responsible for return costs unless goods are faulty or misdescribed.',
    'This right does not apply to personalised, perishable or time‑sensitive items.',
  ] },
  { n: 13, title: 'Intellectual Property Rights', body: [
    'All content (text, graphics, logos, images, audio, video, downloads, software) is the property of Motion Reality Sim Racing Ltd or its suppliers.',
    'Content is protected by UK and international copyright, trademark and intellectual property laws.',
    'You may not reproduce, distribute, modify or create derivative works without prior written consent.',
    'Commercial use of our content is strictly prohibited unless expressly authorised.',
  ] },
  { n: 14, title: 'Acceptance of Terms', body: [
    'By ticking the checkbox during checkout or account registration, you confirm that you have read, understood and agreed to these Terms and Conditions.',
    'Continued use of the website also constitutes acceptance.',
    'This acceptance forms a legally binding agreement under UK law, including the Consumer Rights Act 2015 and Electronic Commerce Regulations 2002.',
  ] },
  { n: 15, title: 'Loyalty Programme', body: [
    'The Motion Reality loyalty programme rewards customers for shopping with us. It operates as two distinct mechanisms: spendable points (earned on shop purchases) and a free-session tally (earned by booking experiences).',
    'Points: Customers earn 1 point per £1 spent on shop product purchases. Experience bookings do not earn spendable points. Points are redeemable in blocks of 100 points = £1 off, issued as a single-use discount code applied at checkout.',
    'Free-session tally: Every confirmed or completed experience booking counts as one qualifying session. After 20 qualifying sessions, the customer earns one free experience session. Loyalty-free (redeemed) sessions do not count toward the next free session.',
    'Tiers: Lifetime earned points determine the customer\'s tier (Bronze, Silver, Gold, Platinum), unlocking bonus points and benefits at each threshold. Tier benefits are displayed on the customer\'s account page.',
    'Inactivity expiry: Points balances and the free-session tally expire after 12 consecutive months of account inactivity. Activity is defined as any shop purchase, experience booking, or points redemption. When the 12-month threshold is reached, the entire points balance is zeroed and the free-session tally resets to zero.',
    'Warning emails: Customers receive warning emails 60 days, 30 days, and 7 days before their rewards are due to expire, giving them the opportunity to place an order or book a session and reset their activity clock.',
    'Points and free sessions have no cash value, cannot be transferred between accounts, and may not be exchanged for cash. We reserve the right to modify the earn rate, redemption rate, tier thresholds, or expiry period with reasonable notice. Any changes will be reflected on this page.',
  ] },
];

const COMPANY = {
  name: 'Motion Reality Sim Racing Ltd',
  reg: '16171282',
  office: '3rd Floor, 86‑90 Paul Street, London, England, EC2A 4NE',
  vat: 'GB491158870',
  email: 'sales@motionreality.co.uk',
  phone: '0151 528 3001',
  sort: '30-54-66',
  account: '78152960',
  iban: 'GB55LOYD30546678152960',
  bic: 'LOYDGB21F95',
};

export default function TermsConditions() {
  useSeo({
    title: 'Terms & Conditions — Motion Reality Sim Racing Ltd',
    description: 'Terms and Conditions for Motion Reality Sim Racing Ltd, governing the use of our website and the purchase of sim racing products and experiences.',
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">LEGAL</p>
            <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-3">
              Terms & Conditions
            </h1>
            <p className="text-white/40 text-sm">
              Last updated: September 2026 · These terms apply to all use of motionreality.co.uk and purchases from Motion Reality Sim Racing Ltd.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Information callout (Section 11) */}
      <section className="px-6 lg:px-10 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#111115] border border-white/5 p-7">
            <div className="flex items-center gap-2 mb-5">
              <Building2 className="w-4 h-4 text-primary" />
              <p className="text-primary text-[10px] font-bold tracking-widest">SECTION 11 · COMPANY INFORMATION</p>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">COMPANY NAME</dt><dd className="text-white/80">{COMPANY.name}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">REGISTRATION NUMBER</dt><dd className="text-white/80">{COMPANY.reg}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">REGISTERED OFFICE</dt><dd className="text-white/80">{COMPANY.office}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">VAT NUMBER</dt><dd className="text-white/80">{COMPANY.vat}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">SORT CODE</dt><dd className="text-white/80 font-mono">{COMPANY.sort}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">ACCOUNT NUMBER</dt><dd className="text-white/80 font-mono">{COMPANY.account}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">IBAN</dt><dd className="text-white/80 font-mono">{COMPANY.iban}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">BIC / SWIFT</dt><dd className="text-white/80 font-mono">{COMPANY.bic}</dd></div>
              <div>
                <dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">EMAIL</dt>
                <dd><a href={`mailto:${COMPANY.email}`} className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{COMPANY.email}</a></dd>
              </div>
              <div>
                <dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">TELEPHONE</dt>
                <dd><a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{COMPANY.phone}</a></dd>
              </div>
            </dl>
            <p className="text-white/30 text-xs mt-5 pt-5 border-t border-white/5">
              Provided in accordance with the Electronic Commerce (EC Directive) Regulations 2002 and the Companies Act 2006.
            </p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="px-6 lg:px-10 pb-20">
        <div className="max-w-3xl mx-auto space-y-10">
          {SECTIONS.map((s) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-primary font-mono font-bold text-sm">{String(s.n).padStart(2, '0')}</span>
                <h2 className="text-white font-black text-lg uppercase tracking-wide">{s.title}</h2>
              </div>
              <div className="space-y-3 pl-8">
                {s.body.map((p, i) => (
                  <p key={i} className="text-white/55 text-sm leading-relaxed">{p}</p>
                ))}
                {s.list && (
                  <ul className="space-y-2 mt-1">
                    {s.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-white/55 text-sm leading-relaxed">
                        <span className="text-primary mt-1.5 shrink-0">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.after?.map((p, i) => (
                  <p key={`a${i}`} className="text-white/55 text-sm leading-relaxed">{p}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}